import {DateTime} from 'luxon';
import LocalStorage from "./localStorage";

const userTimezone = () => {
  const authUser = LocalStorage.getAuthUser();

  if(authUser) {
    return authUser.timezone
  }

  console.warn('Forefront Warning: Auth user not found. So dates will go off the computers local time');
  return null
}

/*
   * These are private functions used in the functions below
   */
function timestampInUserTimezone(timestamp) {
  return DateTime.fromISO(timestamp, {zone: 'UTC'}).setZone(userTimezone())
}

function nowInUserTimezone(plusHour, zeroMinutes) {
  let now = DateTime.now().setZone(userTimezone())

  if (plusHour) now = now.plus({hour: plusHour});

  if (zeroMinutes) now = now.set({minute: 0})

  return now;
}

function yesterdayInUserTimezone(hour, minute) {
  let yesterday = DateTime.now().setZone(userTimezone()).minus({days:1})

  if (hour) yesterday = yesterday.set({hour: hour});

  if (minute) yesterday = yesterday.set({minute: minute});

  return yesterday;
}

function todayInUserTimezone(hour, minute) {
  let today = DateTime.now().setZone(userTimezone())

  if (hour) today = today.set({hour: hour});

  if (minute) today = today.set({minute: minute});

  return today;
}

function tomorrowInUserTimezone(hour, minute) {
  let tomorrow = DateTime.now().setZone(userTimezone()).plus({days:1})

  if (hour) tomorrow = tomorrow.set({hour: hour});

  if (minute) tomorrow = tomorrow.set({minute: minute});

  return tomorrow;
}

function futureInUserTimezone(days = 1, hour, minute) {
  let tomorrow = DateTime.now().setZone(userTimezone()).plus({days:days})

  if (hour) tomorrow = tomorrow.set({hour: hour});

  if (minute) tomorrow = tomorrow.set({minute: minute});

  return tomorrow;
}

function timestampToDatetime(timestamp) {
  return DateTime.fromISO(timestamp)
}

function timestampToDateOnly(timestamp) {
  return DateTime.fromISO(timestamp, {zone: 'UTC'})
}

const dates = {
  /*
   * The convert... functions take something and return something else
   */
  convertDatetimeToISO(datetime) {
    return datetime.toISO();
  },
  convertDatetimeToJSDate(datetime) {
    return datetime.toJSDate();
  },
  convertJSDateToISO(jsDate) {
    return DateTime.fromJSDate(jsDate).toISO();
  },
  convertJsDateToStartOf(jsDate) {
    return DateTime.fromJSDate(jsDate).startOf('day').toISO();
  },
  convertJsDateToEndOf(jsDate) {
    return DateTime.fromJSDate(jsDate).endOf('day').toISO();
  },
  /*
   * The formatAs.. functions will return strings as specified
   */
  formatAsDate(timestamp, convertToUserTimezone = true, isDateOnly = false) {
    if (!timestamp) return '';

    if(isDateOnly) {
      return timestampToDateOnly(timestamp).toLocaleString();
    } else if(convertToUserTimezone) {
      return timestampInUserTimezone(timestamp).toLocaleString();
    } else {
      return timestampToDatetime(timestamp).toLocaleString();
    }
  },
  formatAsDateTime(timestamp, convertToUserTimezone = true) {
    if (!timestamp) return '';

    if(convertToUserTimezone) {
      return timestampInUserTimezone(timestamp).toLocaleString(DateTime.DATETIME_SHORT);
    } else {
      return timestampToDatetime(timestamp).toLocaleString(DateTime.DATETIME_SHORT);
    }
  },
  formatAsDay(timestamp, convertToUserTimezone = true) {
    if (!timestamp) return '';

    if(convertToUserTimezone) {
      return timestampInUserTimezone(timestamp).day;
    } else {
      return timestampToDatetime(timestamp).day;
    }
  },
  formatAsDBDate(timestamp, convertToUserTimezone = true) {
    if (!timestamp) return '';

    if(convertToUserTimezone) {
      return timestampInUserTimezone(timestamp).toFormat('yyyy-MM-dd');
    } else {
      return timestampToDatetime(timestamp).toFormat('yyyy-MM-dd');
    }
  },
  formatAsMonth(timestamp, convertToUserTimezone = true) {
    if (!timestamp) return '';

    if(convertToUserTimezone) {
      return timestampInUserTimezone(timestamp).month;
    } else {
      return timestampToDatetime(timestamp).month;
    }
  },
  formatAsMonthLong(timestamp, convertToUserTimezone = true) {
    if (!timestamp) return '';

    if(convertToUserTimezone) {
      return timestampInUserTimezone(timestamp).monthLong;
    } else {
      return timestampToDatetime(timestamp).monthLong;
    }
  },
  formatAsMonthShort(timestamp, convertToUserTimezone = true) {
    if (!timestamp) return '';

    if(convertToUserTimezone) {
      return timestampInUserTimezone(timestamp).monthShort;
    } else {
      return timestampToDatetime(timestamp).monthShort;
    }
  },
  formatAsTime(timestamp, convertToUserTimezone = true) {
    if (!timestamp) return '';

    if(convertToUserTimezone) {
      return timestampInUserTimezone(timestamp).toLocaleString(DateTime.TIME_SIMPLE);
    } else {
      return timestampToDatetime(timestamp).toLocaleString(DateTime.TIME_SIMPLE);
    }
  },
  formatAsYear(timestamp, convertToUserTimezone = true) {
    if (!timestamp) return '';

    if(convertToUserTimezone) {
      return timestampInUserTimezone(timestamp).year;
    } else {
      return timestampToDatetime(timestamp).year;
    }
  },
  /*
   * The get... functions return an ISO string as specified
   */
  getHour(timestamp, plus) {
    let date = timestampInUserTimezone(timestamp)

    if(plus) {
      date = date.plus({hour: plus})
    }

    return date.hour;
  },
  getISO(timestamp) {
    if (!timestamp) return '';
    return timestampInUserTimezone(timestamp).toISO();
  },
  getJSDate(timestamp) {
    if (!timestamp) return '';
    return timestampInUserTimezone(timestamp).toJSDate();
  },
  getLocalTimezone() {
    return DateTime.local().zoneName;
  },
  getMinute(timestamp, plus) {
    let date = timestampInUserTimezone(timestamp)

    if(plus) {
      date = date.plus({minute: plus})
    }

    return date.minute;
  },
  getNowISO(plusHour, zeroMinutes) {
    return nowInUserTimezone(plusHour, zeroMinutes).toISO();
  },
  getNowJSDate(plusHour, zeroMinutes) {
    return nowInUserTimezone(plusHour, zeroMinutes).toJSDate();
  },
  getTodayISO(hour, minute) {
    return todayInUserTimezone(hour, minute).toISO();
  },
  getTodayTime(hour, minute) {
    return todayInUserTimezone(hour, minute).toLocaleString(DateTime.TIME_SIMPLE);
  },
  getTodayJSDate(hour, minute) {
    return todayInUserTimezone(hour, minute).toJSDate();
  },
  getTomorrowISO(hour, minute) {
    return tomorrowInUserTimezone(hour, minute).toISO();
  },
  getTomorrowJSDate(hour, minute) {
    return tomorrowInUserTimezone(hour, minute).toJSDate();
  },
  getFutureISO(day, hour, minute) {
    return futureInUserTimezone(day, hour, minute).toISO();
  },
  getFutureJSDate(day, hour, minute) {
    return futureInUserTimezone(day, hour, minute).toJSDate();
  },
  /*
   * the is... functions return boolean as specified
   */
  isAfter(timestamp, afterTimestamp) {
    return timestampInUserTimezone(timestamp) >= timestampInUserTimezone(afterTimestamp);
  },
  isBefore(timestamp, beforeTimestamp) {
    return timestampInUserTimezone(timestamp) <= timestampInUserTimezone(beforeTimestamp);
  },
  isInFuture(timestamp) {
    if (!timestamp) return '';
    return timestampInUserTimezone(timestamp) > nowInUserTimezone();
  },
  isInPast(timestamp) {
    if (!timestamp) return '';
    return timestampInUserTimezone(timestamp) < nowInUserTimezone();
  },
  isInPastAmountOfDays(timestamp, days) {
    if (!timestamp) return '';
    return timestampInUserTimezone(timestamp).minus({days: days}) < nowInUserTimezone();
  },
  isToday(timestamp) {
    if (!timestamp) return '';
    return timestampInUserTimezone(timestamp).hasSame(nowInUserTimezone(), 'day');
  },
  isTodayOrInFuture(timestamp) {
    if (!timestamp) return '';
    return timestampInUserTimezone(timestamp) >= nowInUserTimezone();
  },
  isTodayOrInPast(timestamp) {
    if (!timestamp) return '';
    return this.isToday(timestamp) || this.isInPast(timestamp)
  },
  isDateEqual(timestamp, compareTimestamp) {
    return timestampInUserTimezone(timestamp).hasSame(timestampInUserTimezone(compareTimestamp), 'day');
  },
  /*
   * this remove... functions will remove items from array based date prop and on specified timeframe
   */
  removeFutureItems(items, prop) {
    return items.filter((item) => {
      return timestampInUserTimezone(item[prop]) < nowInUserTimezone();
    });
  },
  removePastItems(items, prop) {
    return items.filter((item) => {
      return timestampInUserTimezone(item[prop]) > nowInUserTimezone();
    });
  },
  /*
   * this return... functions will return items from array based date prop and on specified timeframe
   */
  returnItemsBeforeYesterday(items, prop) {
    return items.filter((item) => {
      // If the prop date is null then it's past due so this is true
      if(!item[prop]) return true

      return timestampInUserTimezone(item[prop]).startOf('day') < yesterdayInUserTimezone().startOf('day');
    });
  },
  returnItemsForYesterday(items, prop) {
    return items.filter((item) => {
      // If the prop date is null then it's past due so this is false
      if(!item[prop]) return false

      return timestampInUserTimezone(item[prop]).hasSame(yesterdayInUserTimezone(), 'day');
    });
  },
  returnItemsBeforeToday(items, prop) {
    return items.filter((item) => {
      // If the prop date is null then it's past due so this is true
      if(!item[prop]) return true

      return timestampInUserTimezone(item[prop]).startOf('day') < nowInUserTimezone().startOf('day');
    });
  },
  returnItemsForToday(items, prop) {
    return items.filter((item) => {
      // If the prop date is null then it's past due so this is false
      if(!item[prop]) return false

      return timestampInUserTimezone(item[prop]).hasSame(nowInUserTimezone(), 'day');
    });
  },
  returnItemsForTomorrow(items, prop) {
    return items.filter((item) => {
      // If the prop date is null then it's past due so this is false
      if(!item[prop]) return false

      return timestampInUserTimezone(item[prop]).hasSame(tomorrowInUserTimezone(), 'day');
    });
  },
  returnItemsForAfterToday(items, prop) {
    return items.filter((item) => {
      // If the prop date is null then it's past due so this is false
      if(!item[prop]) return false

      return timestampInUserTimezone(item[prop]).startOf('day') > nowInUserTimezone().startOf('day');
    });
  },
  returnItemsForAfterTomorrow(items, prop) {
    return items.filter((item) => {
      // If the prop date is null then it's past due so this is false
      if(!item[prop]) return false

      return timestampInUserTimezone(item[prop]).startOf('day') > tomorrowInUserTimezone().startOf('day');
    });
  },
  returnItemsUpcoming(items, prop) {
    return items.filter((item) => {
      // If the prop date is null then it's past due so this is false
      if(!item[prop]) return false

      const isAfterTomorrow = timestampInUserTimezone(item[prop]).startOf('day') > tomorrowInUserTimezone().startOf('day');
      const isBefore7Days = timestampInUserTimezone(item[prop]).startOf('day') < futureInUserTimezone(7).endOf('day');

      return isAfterTomorrow && isBefore7Days;
    });
  },
  /*
   * this set... functions will return luxon datetime that can be used with convert functions
   */
  setTime(timestamp, hour, minute){
    let date = timestampInUserTimezone(timestamp);

    if(hour) {
      date = date.set({hour: hour});
    }

    if(minute) {
      date = date.set({minute: minute});
    }

    return date;
  },
  setToUTC(datetime) {
    return datetime.toUTC();
  },
  /*
   * this startOf... functions will return iso of specific time
   */
  startOfLast7Days() {
    return todayInUserTimezone().minus({days: 6}).startOf('day').toISO();
  },
  startOfLastXDays(days) {
    // 1 will be start of today
    return todayInUserTimezone().minus({days: days - 1}).startOf('day').toISO();
  },
  startOfLastXYears(years) {
    // 1 will be start of this year
    return todayInUserTimezone().minus({years: years - 1}).startOf('years').toISO();
  },
  endOfLastXDays(days) {
    // Includes Today
    return todayInUserTimezone().minus({days: days - 1}).endOf('day').toISO();
  },
  startOfLast30Days() {
    return todayInUserTimezone().minus({days: 29}).startOf('day').toISO();
  },
  startOfLast365Days() {
    return todayInUserTimezone().minus({days: 364}).startOf('day').toISO();
  },
  startOfLastMonth() {
    return todayInUserTimezone().minus({months: 1}).startOf('month').toISO();
  },
  startOfLastWeek() {
    return todayInUserTimezone().minus({weeks: 1}).startOf('week').toISO();
  },
  startOfLastYear() {
    return todayInUserTimezone().minus({years: 1}).startOf('year').toISO();
  },
  startOfThisMonth() {
    return todayInUserTimezone().startOf('month').toISO();
  },
  startOfThisWeek() {
    return todayInUserTimezone().startOf('week').toISO();
  },
  startOfThisYear() {
    return todayInUserTimezone().startOf('year').toISO();
  },
  startOfToday() {
    return todayInUserTimezone().startOf('day').toISO();
  },
  startOfYesterday() {
    return todayInUserTimezone().minus({days: 1}).startOf('day').toISO();
  },
  startOfTimestamp(timestamp) {
    if(timestamp) return null
    return timestampInUserTimezone(timestamp).startOf('day').toISO();
  },
  /*
   * this endOf... functions will return iso of specific time
   */
  endOfLastMonth() {
    return todayInUserTimezone().minus({months: 1}).endOf('month').toISO();
  },
  endOfLastWeek() {
    return todayInUserTimezone().minus({weeks: 1}).endOf('week').toISO();
  },
  endOfLastYear() {
    return todayInUserTimezone().minus({years: 1}).endOf('year').toISO();
  },
  endOfThisMonth() {
    return todayInUserTimezone().endOf('month').toISO();
  },
  endOfThisWeek() {
    return todayInUserTimezone().endOf('week').toISO();
  },
  endOfThisYear() {
    return todayInUserTimezone().endOf('year').toISO();
  },
  endOfToday() {
    return todayInUserTimezone().endOf('day').toISO();
  },
  endOfYesterday() {
    return todayInUserTimezone().minus({days: 1}).endOf('day').toISO();
  },
  endOfTimestamp(timestamp) {
    if(timestamp) return null
    return timestampInUserTimezone(timestamp).endOf('day').toISO();
  },
  /*
   * misc functions
   */
  addUserTimezone(timestamp) {
    return DateTime.fromISO(timestamp).setZone(userTimezone(), {keepLocalTime: true});
  },
  addDay(timestamp) {
    return timestampInUserTimezone(timestamp).plus({days: 1}).toISO();
  },
  subtractDay(timestamp) {
    return timestampInUserTimezone(timestamp).minus({days: 1}).toISO();
  },
  subtractDates(timestamp1, timestamp2) {
    return DateTime.subtractDates(timestamp1, timestamp2);
  },
  lastTwelveMonths() {
    let months = [];
    let monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    let date = new Date();
    date.setDate(1);
    for (let i=11; i>=0; i--) {
      months.unshift(monthNames[date.getMonth()]);
      date.setMonth(date.getMonth() - 1);
    }

    return months;
  }
};

export default dates;
